/**
 * Settings → Organization Workflow (CC-V35-BANK-DEMO-READY-01).
 *
 * The administrator-facing, natural-language control over the organization's
 * experience profile. This surface OWNS no persistence of its own: the only
 * write is the existing audited PUT /api/experience-profile (JWT-org-only,
 * strict body, atomic mutation + append-only history + audit). Internal
 * profile keys and versions never render — the two workflows are described
 * in product language, and the mapping to registry tuples lives in code.
 *
 * On a successful save, in order: refresh the experience-profile boundary
 * (org-epoch cache invalidation + every mounted consumer re-resolves +
 * cross-tab broadcast), then redirect this session to the new workflow's
 * landing path. A failed save changes nothing: the previous workflow stays
 * selected (selection derives from server state, never from the click), an
 * explicit error is shown, and no refresh or navigation happens.
 *
 * Workflow selection is presentation routing only — it grants no ARC
 * Library entitlement and issues no other mutation.
 */

import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useApiClient } from '../../hooks/useApiClient';
import { useExperienceProfile } from '../../hooks/useExperienceProfile';
import { refreshExperienceProfile } from '../../hooks/experienceProfileRefresh';
import { useOrganizationWorkflowContext } from '../../hooks/useOrganizationWorkflowContext';
import {
  formatChangedAt,
  formatChangedBy,
  formatHoldings,
} from '../../config/workflowContextCopy';
import {
  EXPERIENCE_PROFILES,
  type ExperienceProfileDefinition,
} from '../../config/experienceProfiles';
import { FocusTrapDialog } from '../banking/boardKit';
import './organization-workflow.css';

const DEFAULT_AUDIT_REASON = 'Changed through Organization Workflow settings.';

interface WorkflowOption {
  /** DOM-safe id — natural language only, never an internal profile key. */
  id: string;
  label: string;
  description: string;
  contextLabel: string;
  /** Internal registry key — code-side mapping only, never rendered. */
  registryKey: ExperienceProfileDefinition['key'];
}

const WORKFLOW_OPTIONS: WorkflowOption[] = [
  {
    id: 'ai-use-case-driven',
    label: 'AI use case–driven workflow',
    description:
      'Start with AI use cases, map them to applicable controls, and validate the assessment scope before running readiness checks.',
    contextLabel: 'Banking experience',
    registryKey: 'banking_use_case',
  },
  {
    id: 'dataset-driven',
    label: 'Dataset-driven workflow',
    description:
      'Start with datasets and progress through data quality, FAIR, contextual-validity, and AI-readiness assessments.',
    contextLabel: 'Core DATA Compass experience',
    registryKey: 'core_dataset',
  },
];

export const OrganizationWorkflow: React.FC = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const profile = useExperienceProfile();
  const workflowContext = useOrganizationWorkflowContext();
  const { put } = useApiClient();
  const navigate = useNavigate();

  const [pendingOption, setPendingOption] = useState<WorkflowOption | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invokingControlRef = useRef<HTMLElement | null>(null);

  const currentKey =
    profile.status === 'resolved' ? profile.definition.key : null;

  const currentOption = useMemo(
    () => WORKFLOW_OPTIONS.find((o) => o.registryKey === currentKey) ?? null,
    [currentKey]
  );

  // Naming the organization is half the guard: an administrator who manages
  // more than one workspace has to see WHICH one they are about to change.
  const organizationName =
    workflowContext.status === 'ready'
      ? workflowContext.context.organizationName
      : null;

  if (profile.status === 'pending') {
    return (
      <div className="org-workflow" aria-busy="true" role="status">
        <span className="sr-only">Loading workspace settings…</span>
      </div>
    );
  }

  if (profile.status !== 'resolved') {
    // Fail closed exactly like the rest of the profile surface: no control,
    // no fallback selection, no mutation path.
    return (
      <div className="org-workflow" role="alert">
        <h2>Workspace starting point</h2>
        <p>
          Your organization&rsquo;s workspace configuration could not be resolved.
          Contact support before making changes.
        </p>
      </div>
    );
  }

  const handleSelect = (
    option: WorkflowOption,
    target: HTMLElement | null
  ) => {
    if (!isAdmin || saving) return;
    if (option.registryKey === currentKey) return;
    invokingControlRef.current = target;
    setError(null);
    setNote('');
    setPendingOption(option);
  };

  const closeDialog = () => {
    if (saving) return;
    setPendingOption(null);
    setNote('');
    setError(null);
  };

  const handleConfirm = async () => {
    if (!pendingOption) return;
    const target = EXPERIENCE_PROFILES.find(
      (p) => p.key === pendingOption.registryKey
    );
    if (!target) {
      setError('This workflow is not available in this deployment.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await put('/api/experience-profile', {
        key: target.key,
        version: target.version,
        reason: note.trim() || DEFAULT_AUDIT_REASON,
      });

      // Order matters: the refresh boundary first (org-epoch invalidation,
      // consumer re-resolution, cross-tab broadcast), then the same-session
      // redirect. The destination gate renders its neutral pending state
      // while the fresh profile is in flight — no denial flash, no logout.
      refreshExperienceProfile();
      setPendingOption(null);
      setNote('');
      setSaving(false);
      navigate(target.landingPath);
    } catch (e) {
      setSaving(false);
      setError(
        e instanceof Error && e.message
          ? e.message
          : 'The workflow change was not applied.'
      );
    }
  };

  return (
    <div className="org-workflow" data-testid="org-workflow">
      <h2>Workspace starting point</h2>
      <p className="org-workflow-supporting">
        Choose how your organization begins its work in DATA Compass. This
        setting applies to everyone in your organization.
      </p>

      {!isAdmin && (
        <p className="org-workflow-readonly-note">
          Only an organization administrator can change this setting. The
          current workflow is shown below.
        </p>
      )}

      <div
        className="org-workflow-options"
        role="radiogroup"
        aria-label="Workspace starting point"
      >
        {WORKFLOW_OPTIONS.map((option) => {
          const checked = option.registryKey === currentKey;
          return (
            <label
              key={option.id}
              className={`org-workflow-option${checked ? ' is-current' : ''}`}
              htmlFor={`workflow-${option.id}`}
            >
              <input
                type="radio"
                id={`workflow-${option.id}`}
                name="organization-workflow"
                checked={checked}
                disabled={!isAdmin || saving}
                onChange={(event) =>
                  handleSelect(option, event.currentTarget)
                }
                aria-describedby={`workflow-${option.id}-desc`}
              />
              <span className="org-workflow-option-body">
                <span className="org-workflow-option-head">
                  <span className="org-workflow-option-label">{option.label}</span>
                  <span className="org-workflow-context">{option.contextLabel}</span>
                  {checked && (
                    <span className="org-workflow-current-badge">Current</span>
                  )}
                </span>
                <span
                  className="org-workflow-option-desc"
                  id={`workflow-${option.id}-desc`}
                >
                  {option.description}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {/* AC bullet 3: the current workflow's provenance, read from the
          existing append-only change history — so a surprise flip is
          diagnosable here at a glance instead of through an incident. */}
      {workflowContext.status === 'ready' && (
        <p className="org-workflow-provenance" data-testid="org-workflow-provenance">
          {workflowContext.context.provenance ? (
            <>
              Current workflow set by{' '}
              <strong>{formatChangedBy(workflowContext.context.provenance)}</strong> on{' '}
              {formatChangedAt(workflowContext.context.provenance.changedAt)}.
            </>
          ) : (
            <>
              No workflow change has been recorded for this organization — it is
              still on the workflow it was provisioned with.
            </>
          )}
        </p>
      )}

      {pendingOption && (
        <FocusTrapDialog
          label="Change workspace starting point"
          onClose={closeDialog}
          restoreFocusTo={invokingControlRef.current}
          backdropClassName="org-workflow-modal-backdrop"
          dialogClassName="org-workflow-modal"
        >
          <h3>Change workspace starting point?</h3>
          <p>
            Switching {organizationName ?? 'your organization'} to the{' '}
            {pendingOption.label} changes the starting experience for everyone
            in your organization, immediately.
            {currentOption ? (
              <>
                {' '}
                Your organization currently uses the {currentOption.label}.
              </>
            ) : null}
          </p>

          {/* The consequence the 2026-08-17 incident had no way to show: what
              this organization actually holds under the workflow being left.
              An unreadable count says so — it never renders as zero. */}
          {workflowContext.status === 'ready' && (
            <p className="org-workflow-holdings" data-testid="org-workflow-holdings">
              Under {currentOption ? `the ${currentOption.label}` : 'its current workflow'},{' '}
              {organizationName ?? 'your organization'} holds{' '}
              <strong>{formatHoldings(workflowContext.context.holdings)}</strong>.
              Switching removes the navigation to that work for everyone in the
              organization. Nothing is deleted, and switching back restores it.
            </p>
          )}
          {workflowContext.status === 'unavailable' && (
            <p className="org-workflow-holdings is-unknown" data-testid="org-workflow-holdings-unknown">
              This organization&rsquo;s dataset and analysis counts could not be
              read just now, so this confirmation cannot tell you what switching
              will hide.
            </p>
          )}
          <div className="org-workflow-note-field">
            <label htmlFor="org-workflow-note">Administrator note (optional)</label>
            <textarea
              id="org-workflow-note"
              value={note}
              maxLength={500}
              rows={3}
              placeholder="Why is this change being made?"
              onChange={(event) => setNote(event.target.value)}
              disabled={saving}
            />
          </div>
          {error && (
            <p className="org-workflow-error" role="alert">
              {error}
            </p>
          )}
          <div className="org-workflow-modal-actions">
            <button
              type="button"
              className="org-workflow-btn"
              onClick={closeDialog}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="org-workflow-btn is-primary"
              onClick={() => void handleConfirm()}
              disabled={saving}
            >
              {saving ? 'Applying…' : 'Confirm change'}
            </button>
          </div>
        </FocusTrapDialog>
      )}
    </div>
  );
};

export default OrganizationWorkflow;
